import React, { useRef, useState, useEffect } from "react";
import { FlexSpaceBetween } from "styles/global-style";
import { Draggable } from "@hello-pangea/dnd";
import useKey from "utils/KeyListener";
import PillDropdown from "components/PillDropdown";

export default function Task({ indexPath, task, title, clickTaskCb, user, selected, isEditing, onSaveTitle, users, onSaveEstimate, onSaveAssignee, isCompleted }) {
  const titleRef = useRef();
  const pillsRef = useRef();
  const taskContainerRef = useRef(null);
  const [titleValue, setTitleValue] = useState(title || "");
  const inputRef = useRef(null);
  const estimateRef = useRef(null);
  const assigneeRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(100);
  // eslint-disable-next-line no-unused-vars
  const [activeField, setActiveField] = useState('title'); // 'title', 'estimate', 'assignee' - only setActiveField is used
  const preventBlurRef = useRef(false);
  const [editingEstimate, setEditingEstimate] = useState(false);
  const [editingAssignee, setEditingAssignee] = useState(false);

  // Function to get text width using canvas
  const getTextWidth = (text, useTitleRef = false) => {
    if (!text) return 0;
    
    // Get computed styles from either input or title div for accurate measurement
    const element = useTitleRef ? titleRef.current : inputRef.current;
    if (!element) return 0;
    
    const computedStyle = window.getComputedStyle(element);
    const font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    return context.measureText(text).width;
  };

  // Sync titleValue with title prop and calculate width on load
  useEffect(() => {
    setTitleValue(title || "");
    
    // Calculate width on load using title div's styles
    // Use actual title text, or "Untitled Task" if empty
    const textToMeasure = title || "Untitled Task";
    requestAnimationFrame(() => {
      if (titleRef.current) {
        const textWidth = getTextWidth(textToMeasure, true);
        const paddingWidth = 4; // 2px on each side
        const width = Math.max(50, textWidth + paddingWidth);
        setInputWidth(width);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  // Focus input when editing starts (only once when isEditing becomes true)
  useEffect(() => {
    if (isEditing && inputRef.current) {
      setActiveField('title');
      inputRef.current.focus();
      // Only select text if it's empty (new task), otherwise place cursor at end
      if (!titleValue) {
        inputRef.current.select();
      } else {
        inputRef.current.setSelectionRange(titleValue.length, titleValue.length);
      }
      // Measure text width for auto-sizing
      requestAnimationFrame(() => {
        if (titleValue) {
          const textWidth = getTextWidth(titleValue);
          const paddingWidth = 4; // 2px on each side
          const width = Math.max(50, textWidth + paddingWidth);
          setInputWidth(width);
        } else {
          setInputWidth(50);
        }
      });
    } else {
      setActiveField('title');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]); // Only depend on isEditing, not titleValue - separate effect handles titleValue changes

  // Update width when text changes (separate effect to avoid refocusing)
  useEffect(() => {
    if (isEditing && inputRef.current) {
      const text = titleValue || "";
      // Save cursor position to prevent selection reset
      const cursorPosition = inputRef.current.selectionStart;
      
      if (text) {
        const textWidth = getTextWidth(text);
        const paddingWidth = 4; // 2px on each side
        const width = Math.max(50, textWidth + paddingWidth);
        setInputWidth(width);
      } else {
        setInputWidth(50);
      }
      
      // Restore cursor position
      requestAnimationFrame(() => {
        if (inputRef.current && document.activeElement === inputRef.current) {
          inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    }
  }, [titleValue, isEditing]);
  
  // Handle clicks outside the task to end editing
  useEffect(() => {
    if (!isEditing) return;
    
    const handleClickOutside = (event) => {
      // Check if click is outside the task container
      if (taskContainerRef.current && !taskContainerRef.current.contains(event.target)) {
        // Check if click is on a dropdown (which might be rendered outside the container)
        // Dropdowns from PillDropdown have specific styling
        const isDropdownClick = event.target.closest('div[style*="z-index: 1000"]') ||
                                event.target.closest('div[style*="position: absolute"]') ||
                                // Also check if clicking on input fields that might be part of dropdowns
                                (event.target.tagName === 'INPUT' && 
                                 (event.target === estimateRef.current?.getRef?.() || 
                                  event.target === assigneeRef.current?.getRef?.()));
        
        if (!isDropdownClick) {
          // Get the actual current value from the input field (more reliable than state)
          // Always read from the input element first, as state might not be updated yet
          const inputValue = inputRef.current?.value;
          const currentTitle = inputValue !== undefined 
            ? inputValue.trim()
            : titleValue.trim();
          
          // Save title and end editing
          if (onSaveTitle) {
            // Pass null if no title to trigger deletion logic in Work.js
            // Pass the trimmed value if there is one
            onSaveTitle(currentTitle || null);
          }
          // End editing by saving assignee (which will trigger setEditingTask(null) in Work.js)
          if (onSaveAssignee) {
            onSaveAssignee(task?.owner_id || null);
          }
        }
      }
    };
    
    // Use mousedown instead of click to catch the event before other handlers
    document.addEventListener('mousedown', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isEditing, titleValue, onSaveTitle, onSaveAssignee, task?.owner_id]);

  const getStyle = (draggableStyle) => ({
    position: "relative",
    background: isCompleted ? "#a6ff0088" : "#fff",
    borderRadius: 5,
    padding: "6px 11px",
    margin: 3,
    userSelect: "none",
    minHeight: 19,
    cursor: "pointer",
    boxShadow: "rgba(0, 0, 0, 0.667) 1px 3px 5px",
    color: "black",
    display: "flex",
    alignItems: "center",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  // No need for dynamic height calculation since we're using fixed height

  useKey("Enter", false, () => {
    // Don't handle Enter if an input field (including Search) is focused
    const activeElement = document.activeElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.contentEditable === 'true')) {
      return;
    }
    if (selected) {
      clickTaskCb(task);
    }
  });

  return (
    <Draggable 
      draggableId={indexPath.join("-")} 
      index={indexPath[3]} 
      type="task"
      isDragDisabled={isEditing}
    >
      {(provided, snapshot) => (
        <div 
          {...provided.draggableProps} 
          {...(!isEditing ? provided.dragHandleProps : {})} 
          ref={(node) => {
            provided.innerRef(node);
            taskContainerRef.current = node;
          }}
          style={getStyle(provided.draggableProps.style)} 
          onClick={(e) => {
            if (!isEditing && clickTaskCb) {
              // Only open modal if not clicking the title (which has its own handler)
              const clickedTitle = e.target === titleRef.current || titleRef.current?.contains(e.target);
              if (!clickedTitle) {
                clickTaskCb(task, false);
              }
            }
          }}
        >
          {selected && <div style={{ position: "absolute", left: -2, top: -2, right: -2, bottom: -2, border: "3px solid orange", borderRadius: 6 }} />}
          <FlexSpaceBetween gap={8} style={{ alignItems: "center", width: "100%", height: 23, minWidth: 0 }}>
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={titleValue}
                onChange={(e) => {
                  setTitleValue(e.target.value);
                  // Measure text width using canvas
                  const text = e.target.value || "";
                  if (text && inputRef.current) {
                    const textWidth = getTextWidth(text);
                    const paddingWidth = 4; // 2px on each side
                    const width = Math.max(50, textWidth + paddingWidth);
                    setInputWidth(width);
                  } else {
                    setInputWidth(50);
                  }
                }}
                onBlur={(e) => {
                  // Don't save if we're moving to another field via Tab
                  // Check if the relatedTarget is one of our fields
                  const relatedTarget = e.relatedTarget || document.activeElement;
                  const isMovingToEstimate = estimateRef.current && (
                    relatedTarget === estimateRef.current.getRef?.() ||
                    estimateRef.current.getRef?.()?.contains(relatedTarget)
                  );
                  const isMovingToAssignee = assigneeRef.current && (
                    relatedTarget === assigneeRef.current.getRef?.() ||
                    assigneeRef.current.getRef?.()?.contains(relatedTarget)
                  );
                  
                  if (preventBlurRef.current || isMovingToEstimate || isMovingToAssignee) {
                    preventBlurRef.current = false;
                    return;
                  }
                  if (onSaveTitle) {
                    onSaveTitle(titleValue);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    // Save title first before moving to next field
                    if (onSaveTitle && titleValue.trim()) {
                      onSaveTitle(titleValue.trim());
                    }
                    preventBlurRef.current = true;
                    setActiveField('estimate');
                    setEditingEstimate(true);
                    // Use a longer timeout to ensure the blur has completed
                    setTimeout(() => {
                      if (estimateRef.current && estimateRef.current.focus) {
                        estimateRef.current.focus();
                        // Also try to focus the input directly
                        const input = estimateRef.current.getRef?.();
                        if (input) {
                          input.focus();
                        }
                      }
                    }, 50);
                  } else if (e.key === 'Escape') {
                    setTitleValue(title || "");
                    if (onSaveTitle) {
                      onSaveTitle(null); // Cancel editing
                    }
                  } else if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    e.stopPropagation();
                    // Save title first before moving to next field
                    if (onSaveTitle && titleValue.trim()) {
                      onSaveTitle(titleValue.trim());
                    }
                    preventBlurRef.current = true;
                    setActiveField('estimate');
                    setEditingEstimate(true);
                    // Use a longer timeout to ensure the blur has completed
                    setTimeout(() => {
                      if (estimateRef.current && estimateRef.current.focus) {
                        estimateRef.current.focus();
                        // Also try to focus the input directly
                        const input = estimateRef.current.getRef?.();
                        if (input) {
                          input.focus();
                        }
                      }
                    }, 50);
                  }
                }}
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  borderRadius: 4,
                  padding: "2px",
                  outline: "none",
                  width: `${inputWidth}px`,
                  minWidth: 60,
                  maxWidth: 'none',
                  flex: '0 0 auto',
                }}
                placeholder="Task title..."
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              />
            ) : (
              <div 
                ref={titleRef} 
                style={{ 
                  color: "#000", 
                  fontSize: 12, 
                  fontWeight: 500, 
                  flex: '1 1 0',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (clickTaskCb && task && !isEditing) {
                    // Only trigger inline editing if task title is empty (newly created)
                    // Otherwise, open modal like clicking elsewhere on the task
                    const isEmpty = !title || title.trim() === "";
                    clickTaskCb(task, isEmpty);
                  }
                }}
              >
                {title || "Untitled Task"}
              </div>
            )}
            <div ref={pillsRef} style={{ flex: '0 0 auto', display: 'flex' }}>
              <FlexSpaceBetween gap={4}>
                {isEditing && editingEstimate ? (
                  <div style={{ position: 'relative' }}>
                    <PillDropdown
                      ref={estimateRef}
                      regexp={/\d+[dh]?/g}
                      selectedIdx={task?.estimate ? (task.estimate > 8 && task.estimate % 8 === 0 ? 0 : 1) : -1}
                      hasPrefixNumber
                      prefixNumber={
                        task?.estimate
                          ? task.estimate <= 8 || task.estimate % 8 !== 0
                            ? task.estimate
                            : task.estimate / 8
                          : null
                      }
                      list={[{ value: "days" }, { value: "hrs" }]}
                      placeholder={"Estimate"}
                      onSelect={(item, numberValue) => {
                        // Always store estimate in hrs. I.e. 2 days = 2 * 8 hrs.
                        // numberValue is passed from PillDropdown when hasPrefixNumber mode is active
                        // item.value is "days" or "hrs"
                        const number = numberValue || 0;
                        const _estimate = item.value === "hrs" ? number : number * 8;
                        if (onSaveEstimate) {
                          onSaveEstimate(_estimate);
                        }
                        // Switch to display mode after selection
                        setEditingEstimate(false);
                      }}
                      onClear={() => {
                        if (onSaveEstimate) {
                          onSaveEstimate(null);
                        }
                        setEditingEstimate(false);
                      }}
                      pillStyle={{
                        background: "transparent",
                        color: "gray",
                        borderRadius: 4,
                        fontSize: 11,
                        padding: "2px 4px",
                        minWidth: 60,
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingEstimate(false);
                          preventBlurRef.current = true;
                          setActiveField('assignee');
                          setEditingAssignee(true);
                          setTimeout(() => {
                            if (assigneeRef.current && assigneeRef.current.focus) {
                              assigneeRef.current.focus();
                              const input = assigneeRef.current.getRef?.();
                              if (input) {
                                input.focus();
                              }
                            }
                          }, 50);
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingEstimate(false);
                          preventBlurRef.current = true;
                          setActiveField('assignee');
                          setEditingAssignee(true);
                          setTimeout(() => {
                            if (assigneeRef.current && assigneeRef.current.focus) {
                              assigneeRef.current.focus();
                              const input = assigneeRef.current.getRef?.();
                              if (input) {
                                input.focus();
                              }
                            }
                          }, 50);
                        } else if (e.key === 'Tab' && e.shiftKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingEstimate(false);
                          preventBlurRef.current = true;
                          setActiveField('title');
                          setTimeout(() => {
                            if (inputRef.current) {
                              inputRef.current.focus();
                            }
                          }, 50);
                        }
                      }}
                      onBlur={() => {
                        // Switch to display mode when estimate field loses focus
                        setEditingEstimate(false);
                      }}
                      onFocus={() => {
                        setEditingEstimate(true);
                      }}
                    />
                  </div>
                ) : task.estimate ? (
                  <div 
                    style={{ fontWeight: "bold", color: "#555", fontSize: 11, cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isEditing) {
                        setEditingEstimate(true);
                        setTimeout(() => {
                          if (estimateRef.current && estimateRef.current.focus) {
                            estimateRef.current.focus();
                            const input = estimateRef.current.getRef?.();
                            if (input) {
                              input.focus();
                            }
                          }
                        }, 10);
                      }
                    }}
                  >
                    {task.estimate > 8 ? Math.floor(task.estimate / 8) + "d" + (task.estimate % 8 !== 0 ? " " + (task.estimate % 8) + "h" : "") : task.estimate + "h"}
                  </div>
                ) : isEditing ? (
                  <div 
                    style={{ fontWeight: "bold", color: "#999", fontSize: 11, cursor: 'pointer', fontStyle: 'italic' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingEstimate(true);
                      setTimeout(() => {
                        if (estimateRef.current && estimateRef.current.focus) {
                          estimateRef.current.focus();
                          const input = estimateRef.current.getRef?.();
                          if (input) {
                            input.focus();
                          }
                        }
                      }, 10);
                    }}
                  >
                    Estimate
                  </div>
                ) : null}
                {isEditing && editingAssignee ? (
                  <div style={{ position: 'relative' }}>
                    <PillDropdown
                      ref={assigneeRef}
                      list={(users || []).map((val) => ({
                        value: !val.lastname ? val.firstname : val.firstname + " " + val.lastname.charAt(0),
                        id: val.id,
                      }))}
                      selectedIdx={(users || []).findIndex((item) => item.id === task?.owner_id)}
                      placeholder={"Assignee"}
                      onSelect={(item) => {
                        // Save title first if it exists
                        if (onSaveTitle && titleValue.trim()) {
                          onSaveTitle(titleValue.trim());
                        }
                        // Then save assignee (which will end editing)
                        if (onSaveAssignee) {
                          onSaveAssignee(item.id);
                        }
                        // Switch to display mode after selection
                        setEditingAssignee(false);
                      }}
                      onClear={() => {
                        if (onSaveAssignee) {
                          onSaveAssignee(null);
                        }
                        setEditingAssignee(false);
                      }}
                      pillStyle={{
                        background: "transparent",
                        color: "gray",
                        borderRadius: 4,
                        fontSize: 11,
                        padding: "2px 4px",
                        minWidth: 60,
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          e.stopPropagation();
                          // Save title and end editing when Escape is pressed from assignee field
                          setEditingAssignee(false);
                          if (onSaveTitle) {
                            onSaveTitle(titleValue.trim() || null);
                          }
                          // End editing by saving assignee (which will trigger setEditingTask(null) in Work.js)
                          if (onSaveAssignee) {
                            onSaveAssignee(task?.owner_id || null);
                          }
                        } else if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          // Finish editing when Tab is pressed from assignee field (last field)
                          setEditingAssignee(false);
                          preventBlurRef.current = false;
                          if (assigneeRef.current && assigneeRef.current.getRef) {
                            assigneeRef.current.getRef()?.blur();
                          }
                          if (onSaveTitle && titleValue.trim()) {
                            onSaveTitle(titleValue.trim());
                          }
                          // End editing by saving assignee
                          if (onSaveAssignee) {
                            onSaveAssignee(task?.owner_id || null);
                          }
                        } else if (e.key === 'Tab' && e.shiftKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingAssignee(false);
                          preventBlurRef.current = true;
                          setActiveField('estimate');
                          setEditingEstimate(true);
                          setTimeout(() => {
                            if (estimateRef.current && estimateRef.current.focus) {
                              estimateRef.current.focus();
                              const input = estimateRef.current.getRef?.();
                              if (input) {
                                input.focus();
                              }
                            }
                          }, 50);
                        }
                      }}
                      onBlur={() => {
                        // Switch to display mode when assignee field loses focus
                        setEditingAssignee(false);
                        // Check if focus is moving to another field in the task
                        setTimeout(() => {
                          const activeElement = document.activeElement;
                          const isMovingToTitle = inputRef.current && (
                            activeElement === inputRef.current ||
                            inputRef.current.contains(activeElement)
                          );
                          const isMovingToEstimate = estimateRef.current && (
                            activeElement === estimateRef.current.getRef?.() ||
                            estimateRef.current.getRef?.()?.contains(activeElement)
                          );
                          
                          // Only end editing if not moving to another field
                          if (!isMovingToTitle && !isMovingToEstimate) {
                            // Save title first
                            if (onSaveTitle) {
                              onSaveTitle(titleValue.trim() || null);
                            }
                            // End editing by saving assignee (which will trigger setEditingTask(null) in Work.js)
                            if (onSaveAssignee) {
                              onSaveAssignee(task?.owner_id || null);
                            }
                          }
                        }, 0);
                      }}
                      onFocus={() => {
                        setEditingAssignee(true);
                      }}
                    />
                  </div>
                ) : user ? (
                  <div 
                    style={{
                      width: 23,
                      height: 23,
                      borderRadius: '50%',
                      backgroundColor: user.avatar ? 'transparent' : isCompleted ? '#a6ff0088' : '#999',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 8,
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      position: 'relative',
                      cursor: isEditing ? 'pointer' : 'default'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isEditing) {
                        setEditingAssignee(true);
                        setTimeout(() => {
                          if (assigneeRef.current && assigneeRef.current.focus) {
                            assigneeRef.current.focus();
                            const input = assigneeRef.current.getRef?.();
                            if (input) {
                              input.focus();
                            }
                          }
                        }, 10);
                      }
                    }}
                  >
                    {user.avatar ? (
                      <img 
                        src={user.avatar} 
                        alt={`${user.firstname} ${user.lastname || ''}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.backgroundColor = '#999';
                        }}
                      />
                    ) : null}
                    {!user.avatar && (
                      <span>
                        {user.firstname ? user.firstname.substring(0, 1).toUpperCase() + user.firstname.substring(1, 2).toLowerCase() : ''}{user.lastname ? user.lastname.substring(0, 1).toUpperCase() : ''}
                      </span>
                    )}
                  </div>
                ) : isEditing ? (
                  <div 
                    style={{
                      width: 23,
                      height: 23,
                      borderRadius: '50%',
                      backgroundColor: '#ddd',
                      color: '#999',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 8,
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAssignee(true);
                      setTimeout(() => {
                        if (assigneeRef.current && assigneeRef.current.focus) {
                          assigneeRef.current.focus();
                          const input = assigneeRef.current.getRef?.();
                          if (input) {
                            input.focus();
                          }
                        }
                      }, 10);
                    }}
                  >
                    ?
                  </div>
                ) : null}
              </FlexSpaceBetween>
            </div>
          </FlexSpaceBetween>
        </div>
      )}
    </Draggable>
  );
}

